import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "../../helpers";
import guideHero from "../../assets/guide-page-1.png";
import guideBundle from "../../assets/guide-page-2.jpg";
import toolkitPreview from "../../assets/guide-page-3.jpg";
import {
  PageWrapper,
  Eyebrow,
  Label,
  HeroSection,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  PriceContainer,
  Price,
  OldPrice,
  Actions,
  PrimaryButton,
  ButtonArrow,
  TextLink,
  SmallNote,
  HeroImageWrapper,
  HeroImage,
  GetSection,
  GetItem,
  GetValue,
  GetLabel,
  WhoSection,
  WhoIntro,
  WhoTitle,
  WhoCols,
  WhoCol,
  WhoColTitle,
  WhoColText,
  NotSection,
  NotText,
  InsideSection,
  InsideWrap,
  InsideTitle,
  InsideLead,
  InsideImage,
  PartsGrid,
  Part,
  PartNumber,
  PartTitle,
  PartList,
  PartCount,
  ToolkitSection,
  ToolkitImage,
  ToolkitContent,
  ToolkitTitle,
  ToolkitLead,
  ToolsList,
  ToolItem,
  AuthorSection,
  Quote,
  Bio,
  AuthorName,
  BioText,
  FAQSection,
  FAQTitle,
  FAQList,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  ClosingSection,
  ClosingWrap,
  ClosingTitle,
  BuyContainer,
  ClosingPrice,
  LightButton,
} from "./the-guide.styles";

export const TheGuide = () => {
  const navigate = useNavigate();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const navigateAndTrack = (path: string, event: string) => {
    navigate(path);
    trackEvent(event);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBuyClick = async () => {
    trackEvent("Shop - Get the guide click");
    setIsCheckoutLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const response = await fetch(`${apiUrl}/api/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productSlug: 'unhinged-guide',
          priceId: process.env.REACT_APP_STRIPE_PRICE_ID_UNHINGED_GUIDE
        })
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again or contact support at hello@reneelouise.tech');
      setIsCheckoutLoading(false);
    }
  };

  return (
    <PageWrapper>
      <main>
        {/* HERO */}
        <HeroSection>
          <HeroContent>
            <Eyebrow>The Guide · Digital download</Eyebrow>
            <HeroTitle>The <em>Unhinged</em> Guide to Breaking Into Tech</HeroTitle>
            <HeroSubtitle>
              The exact strategy I would use if I had to start again today, including the systems, templates, AI prompts and tactics I would use to actually land the job.
            </HeroSubtitle>
            <PriceContainer>
              <Price>£39</Price>
              <OldPrice>£69</OldPrice>
              <Label style={{ color: '#D9442B' }}>Founding price</Label>
            </PriceContainer>
            <Actions>
              <PrimaryButton onClick={handleBuyClick} disabled={isCheckoutLoading}>
                {isCheckoutLoading ? 'Loading checkout...' : (
                  <>Get the guide <ButtonArrow aria-hidden="true"><ArrowRight size={18} color="#D9442B" /></ButtonArrow></>
                )}
              </PrimaryButton>
              <TextLink onClick={() => scrollToSection('inside')}>
                See what's inside
              </TextLink>
            </Actions>
            <SmallNote>Instant download · PDF · Fillable & printable worksheets</SmallNote>
          </HeroContent>
          <HeroImageWrapper>
            <HeroImage
              src={guideHero}
              alt="The Unhinged Guide to Breaking Into Tech and its toolkit"
              loading="eager"
            />
          </HeroImageWrapper>
        </HeroSection>

        {/* WHAT YOU GET */}
        <GetSection aria-label="What you get">
          <GetItem>
            <GetValue>20</GetValue>
            <GetLabel>Chapters · 687 pages</GetLabel>
          </GetItem>
          <GetItem>
            <GetValue>22</GetValue>
            <GetLabel>Part toolkit</GetLabel>
          </GetItem>
          <GetItem>
            <GetValue>AI</GetValue>
            <GetLabel>Prompt library</GetLabel>
          </GetItem>
          <GetItem>
            <GetValue>✓</GetValue>
            <GetLabel>Templates &amp; checklists</GetLabel>
          </GetItem>
          <GetItem>
            <GetValue>30</GetValue>
            <GetLabel>Day action plan</GetLabel>
          </GetItem>
        </GetSection>

        {/* WHO IT'S FOR */}
        <WhoSection>
          <WhoIntro>
            <Eyebrow>Who it's for</Eyebrow>
            <WhoTitle>For anyone who wants in, <em>and wants a plan.</em></WhoTitle>
          </WhoIntro>
          <div>
            <WhoCols>
              <WhoCol>
                <Label style={{ color: '#D9442B' }}>01</Label>
                <WhoColTitle>Career switchers</WhoColTitle>
                <WhoColText>
                  Teachers, healthcare workers, finance, retail, operations, creatives. Anyone looking at their current career and thinking there has to be something else.
                </WhoColText>
              </WhoCol>
              <WhoCol>
                <Label style={{ color: '#D9442B' }}>02</Label>
                <WhoColTitle>Recent graduates</WhoColTitle>
                <WhoColText>
                  Including computer science graduates who have the degree but are finding it does not automatically open the door.
                </WhoColText>
              </WhoCol>
            </WhoCols>
            <NotSection>
              <Label style={{ color: '#D9442B' }}>What it will not do</Label>
              <NotText>
                Promise you a job, or six figures in six months. It gives you a system: clear direction, real evidence and a plan you can act on.
              </NotText>
            </NotSection>
          </div>
        </WhoSection>

        {/* INSIDE THE BOOK */}
        <InsideSection id="inside">
          <InsideWrap>
            <Eyebrow>Inside the guide</Eyebrow>
            <InsideTitle>Five parts, from first question to <em>first offer.</em></InsideTitle>
            <InsideLead>
              Written by a former Maths Teacher turned Software Engineer. Every chapter ends with something to do, not just something to read.
            </InsideLead>
            <InsideImage
              src={guideBundle}
              alt="The guide, the toolkit and pages from inside"
              loading="lazy"
            />
            <PartsGrid>
              <Part>
                <PartNumber>Part One</PartNumber>
                <PartTitle>Before You Start</PartTitle>
                <PartList start={1}>
                  <li>Why the Hell Do You Want to Work in Tech?</li>
                  <li>Stop Saying "I Want to Work in Tech"</li>
                  <li>How I Figured Out What I Actually Wanted to Do</li>
                  <li>The Career Experiment</li>
                </PartList>
                <PartCount>4 chapters</PartCount>
              </Part>
              <Part>
                <PartNumber>Part Two</PartNumber>
                <PartTitle>Build Your Roadmap</PartTitle>
                <PartList start={5}>
                  <li>Reverse Engineer the Job</li>
                  <li>How I Would Learn Tech Now</li>
                  <li>The Learning Directory</li>
                </PartList>
                <PartCount>3 chapters</PartCount>
              </Part>
              <Part>
                <PartNumber>Part Three</PartNumber>
                <PartTitle>Prove You Can Do the Job</PartTitle>
                <PartList start={8}>
                  <li>Stop Collecting Certificates. Build Evidence.</li>
                  <li>Build Projects That Actually Prove Something</li>
                  <li>Your Previous Career Is Not Useless</li>
                </PartList>
                <PartCount>3 chapters</PartCount>
              </Part>
              <Part>
                <PartNumber>Part Four</PartNumber>
                <PartTitle>Position Yourself</PartTitle>
                <PartList start={11}>
                  <li>Build a CV That Actually Says Something</li>
                  <li>LinkedIn, GitHub, Portfolios and Your Digital Footprint</li>
                </PartList>
                <PartCount>2 chapters</PartCount>
              </Part>
              <Part>
                <PartNumber>Part Five</PartNumber>
                <PartTitle>Get in the Room</PartTitle>
                <PartList start={13}>
                  <li>How I Actually Approached Breaking In</li>
                  <li>Network Without Being Weird</li>
                  <li>Apply Smarter, Not Just More</li>
                  <li>Build Your Story Bank Before You Need It</li>
                  <li>Use AI as Your Interviewer</li>
                  <li>The Two Week Panic Plan</li>
                  <li>If I Had to Break Into Tech Again Tomorrow</li>
                  <li>Your 30 Day Plan</li>
                </PartList>
                <PartCount>8 chapters</PartCount>
              </Part>
              <Part>
                <PartNumber>Bonus</PartNumber>
                <PartTitle>Keep Your Receipts Once You Get In</PartTitle>
                <PartList>
                  <li>How to keep building evidence once you land the job</li>
                </PartList>
                <PartCount>Bonus chapter</PartCount>
              </Part>
            </PartsGrid>
          </InsideWrap>
        </InsideSection>

        {/* TOOLKIT */}
        <ToolkitSection>
          <ToolkitImage
            src={toolkitPreview}
            alt="Worksheets from The Unhinged Toolkit"
            loading="lazy"
          />
          <ToolkitContent>
            <Eyebrow>The Unhinged Toolkit</Eyebrow>
            <ToolkitTitle>22 tools to fill in, print <em>and use.</em></ToolkitTitle>
            <ToolkitLead>
              Every worksheet, template and prompt from the chapters. Type into them on screen and save your answers, or print just the pages you need.
            </ToolkitLead>
            <ToolsList>
              <ToolItem><span>01</span>Find Your Why Worksheet</ToolItem>
              <ToolItem><span>02</span>Career Exploration Matrix</ToolItem>
              <ToolItem><span>03</span>Career Experiment Scorecard</ToolItem>
              <ToolItem><span>04</span>Informational Interview Template</ToolItem>
              <ToolItem><span>05</span>Informational Interview Question Bank</ToolItem>
              <ToolItem><span>06</span>Job Description Decoder</ToolItem>
              <ToolItem><span>07</span>Skills Gap Analysis</ToolItem>
              <ToolItem><span>08</span>Personal Learning Roadmap</ToolItem>
              <ToolItem><span>09</span>Portfolio Project Planner</ToolItem>
              <ToolItem><span>10</span>Evidence Gap Analysis</ToolItem>
              <ToolItem><span>11</span>Transferable Skills Audit</ToolItem>
              <ToolItem><span>12</span>CV Achievement Builder</ToolItem>
              <ToolItem><span>13</span>Manual Brag Document</ToolItem>
              <ToolItem><span>14</span>Digital Presence Audit</ToolItem>
              <ToolItem><span>15</span>Application Tracker</ToolItem>
              <ToolItem><span>16</span>Application Funnel Diagnostic</ToolItem>
              <ToolItem><span>17</span>Networking Message Templates</ToolItem>
              <ToolItem><span>18</span>Interview Story Bank</ToolItem>
              <ToolItem><span>19</span>STAR + E Builder</ToolItem>
              <ToolItem><span>20</span>Fourteen-Day Interview Preparation Planner</ToolItem>
              <ToolItem><span>21</span>Thirty-Day Break Into Tech Action Plan</ToolItem>
              <ToolItem><span>22</span>Consolidated AI Prompt Library</ToolItem>
            </ToolsList>
          </ToolkitContent>
        </ToolkitSection>

        {/* AUTHOR */}
        <AuthorSection>
          <Quote>The goal is to become demonstrably employable for a specific type of role.</Quote>
          <Bio>
            <Eyebrow>From the author</Eyebrow>
            <AuthorName>Renée Louise</AuthorName>
            <BioText>
              I went from Maths Teacher to Software Engineer, landing my first tech role nine months into my search. Six years later, this is the guide I wish I had when I started.
            </BioText>
            <BioText>
              <TextLink onClick={() => navigateAndTrack("/about", "Shop - About link click")}>
                More about me
              </TextLink>
            </BioText>
          </Bio>
        </AuthorSection>

        {/* FAQ */}
        <FAQSection>
          <div>
            <Eyebrow>Questions</Eyebrow>
            <FAQTitle>Before you <em>buy.</em></FAQTitle>
          </div>
          <FAQList>
            <FAQItem>
              <FAQQuestion>What format is it?</FAQQuestion>
              <FAQAnswer>
                A PDF you can read on any device. The toolkit also comes as separate fillable and printable PDFs with all 22 worksheets.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>Do I need a tech background?</FAQQuestion>
              <FAQAnswer>
                No. It is written for career switchers and new graduates, and helps you work out which role fits before you spend money on courses.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>Which AI tools do the prompts work with?</FAQQuestion>
              <FAQAnswer>
                Any chat assistant. The prompts are plain text: copy them, fill in the brackets and paste them into the tool you already use.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>What is the founding price?</FAQQuestion>
              <FAQAnswer>
                An early-reader price of £39. The standard price will be £69.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>What is your refund policy?</FAQQuestion>
              <FAQAnswer>
                Due to the instant access nature of the guide and toolkit, all sales are final and no refunds are offered. You receive immediate access to the full digital product upon purchase.
              </FAQAnswer>
            </FAQItem>
          </FAQList>
        </FAQSection>

        {/* CLOSING */}
        <ClosingSection id="buy">
          <ClosingWrap>
            <ClosingTitle>Stop saying you want to work in tech. <em>Start the plan.</em></ClosingTitle>
            <BuyContainer>
              <ClosingPrice>
                <Price>£39</Price>
                <OldPrice>£69</OldPrice>
                <Label style={{ color: '#D9442B' }}>Founding price</Label>
              </ClosingPrice>
              <LightButton onClick={handleBuyClick} disabled={isCheckoutLoading}>
                {isCheckoutLoading ? 'Loading checkout...' : (
                  <>Get the guide <ButtonArrow aria-hidden="true"><ArrowRight size={18} color="#D9442B" /></ButtonArrow></>
                )}
              </LightButton>
            </BuyContainer>
          </ClosingWrap>
        </ClosingSection>
      </main>
    </PageWrapper>
  );
};
